import { FC, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreakpoint } from '@yuno/sdk-web-core/dist/hooks/use-breakpoint'
import {
  DropdownItem,
  DropdownList,
  LanguageSelectorContainer,
  MobileCloseButton,
  MobileLanguageItem,
  MobileLanguageList,
  MobileOverlay,
  MobileOverlayHeader,
  MobileSearchInput,
  SelectedValue,
} from './language-selector.styled'

const LANGUAGES: Record<string, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  ru: 'Русский',
  sv: 'Svenska',
  tr: 'Türkçe',
  ja: '日本語',
  ko: '한국어',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'zh-CN': '中文(简体)',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'zh-TW': '中文(繁體)',
  th: 'ภาษาไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  fil: 'Filipino',
  ar: 'العربية',
  hi: 'हिन्दी',
  bn: 'বাংলা',
  ml: 'മലയാളം',
  ur: 'اردو',
  mn: 'Монгол',
}

const GlobeIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2"/>
    <ellipse cx="8" cy="8" rx="2.5" ry="6.5" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="1.5" y1="6" x2="14.5" y2="6" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="1.5" y1="10" x2="14.5" y2="10" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
)

const ChevronIcon: FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}
  >
    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const CloseIcon: FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

export const LanguageSelector: FC = () => {
  const { i18n, t } = useTranslation()
  const { isDesktop } = useBreakpoint()
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const currentLang = i18n.resolvedLanguage ?? 'en'
  const currentName = LANGUAGES[currentLang] ?? currentLang

  const filteredLanguages = Object.entries(LANGUAGES).filter(([, name]) =>
    name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleSelect = (lang: string) => {
    try { localStorage.setItem('yuno-lng', lang) } catch { /* storage unavailable */ }
    i18n.changeLanguage(lang).then(() => window.location.reload())
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleClose = () => {
    setIsOpen(false)
    setSearchQuery('')
  }

  useEffect(() => {
    if (!isDesktop) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isDesktop])

  if (!isDesktop && isOpen) {
    return (
      <MobileOverlay>
        <MobileOverlayHeader>
          <LanguageSelectorContainer onClick={handleClose}>
            <GlobeIcon />
            <SelectedValue>{currentName}</SelectedValue>
            <ChevronIcon isOpen={true} />
          </LanguageSelectorContainer>
          <MobileCloseButton onClick={handleClose}>
            <CloseIcon />
          </MobileCloseButton>
        </MobileOverlayHeader>
        <MobileSearchInput
          placeholder={t('Search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
        <MobileLanguageList>
          {filteredLanguages.map(([code, name]) => (
            <MobileLanguageItem
              key={code}
              isSelected={code === currentLang}
              onClick={() => handleSelect(code)}
            >
              {name}
            </MobileLanguageItem>
          ))}
        </MobileLanguageList>
      </MobileOverlay>
    )
  }

  return (
    <LanguageSelectorContainer ref={containerRef} onClick={() => setIsOpen(prev => !prev)}>
      <GlobeIcon />
      <SelectedValue>{currentName}</SelectedValue>
      <ChevronIcon isOpen={isOpen} />
      {isDesktop && (
        <DropdownList isOpen={isOpen}>
          {Object.entries(LANGUAGES).map(([code, name]) => (
            <DropdownItem
              key={code}
              isSelected={code === currentLang}
              onClick={(e) => { e.stopPropagation(); handleSelect(code) }}
            >
              {name}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </LanguageSelectorContainer>
  )
}
